import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { PublicOrderListItemAction } from '../../enums/public-order-list-item-action';
import {
  PublicOrderCollectionFilter,
  PublicOrderCollectionItem,
  PublicOrderDataSourceService,
} from '@web/shared/module/shop';
import { PublicOrderListCustomCellTemplateComponent } from '../../components/public-order-list-custom-cell-template/public-order-list-custom-cell-template.component';
import { Observable } from 'rxjs';
import { NavigatorService, Route, RouteParam } from '@web/common';

@Component({
  selector: 'app-public-order-list',
  imports: [TableComponent, PublicOrderListCustomCellTemplateComponent],
  templateUrl: './public-order-list.component.html',
  styleUrl: './public-order-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicOrderListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<PublicOrderCollectionItem, PublicOrderCollectionFilter, PublicOrderListItemAction>).create();
  private readonly navigator = inject(NavigatorService);
  private readonly orderDataSource = inject(PublicOrderDataSourceService);

  protected config!: TableConfig<
    PublicOrderCollectionItem,
    PublicOrderCollectionFilter,
    PublicOrderListItemAction
  >;

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .topScrollEnabled(false)
      .responsiveLayout('stack')
      .filtering(false)
      .filters((builder) => [builder.initText('number').build()])
      .itemActions((builder) => [
        builder
          .initMore(PublicOrderListItemAction.More)
          .visibility((item) => this.visibilityForMoreItemAction(item))
          .children((builder) => [
            builder
              .initRouterLink(PublicOrderListItemAction.Preview)
              .icon(Icon.Eye)
              .tooltip('Podgląd')
              .visibility((item) => item.canBeViewed)
              .routerLink(
                (provider) => (item) =>
                  provider.routerLink(Route.PL_AccountOrderDetails, {
                    [RouteParam.Number]: item.number,
                  }),
              )
              .build(),

            builder
              .initCommand(PublicOrderListItemAction.EditShippingAddresses)
              .icon(Icon.Truck)
              .tooltip('Edytuj adres dostawy')
              .visibility((item) => item.canShippingAddressBeUpdated)
              .command((item) => {
                this.navigator.navigateByRouterLink(
                  (provider) =>
                    provider.routerLink(Route.PL_AccountOrderDetails, {
                      [RouteParam.Number]: item.number,
                    }),
                  { fragment: 'lista-przesylek' },
                );
              })
              .build(),
          ])
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initOrdinal()
          .build(),

        builder //
          .initCustom('number')
          .label('Numer')
          .width('8rem')
          .sortKey('number')
          .build(),

        builder //
          .initCustom('totalPrice')
          .width('12rem')
          .label('Kwota')
          .sortKey('totalPrice')
          .filterKey('totalPrice')
          .build(),

        builder //
          .initCustom('status')
          .width('12rem')
          .label('Status')
          .sortKey('status')
          .build(),

        builder //
          .initDate('createdAt')
          .width('12rem')
          .label('Data zamówienia')
          .sortKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<PublicOrderCollectionFilter>,
        ): Observable<TableDataPayload<PublicOrderCollectionItem>> =>
          this.orderDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private visibilityForMoreItemAction(
    item: PublicOrderCollectionItem,
  ): boolean {
    return [item.canBeViewed, item.canShippingAddressBeUpdated].some(Boolean);
  }
}
